// Recent-orders persistence for the no-backend reorder surfaces.
//
// We keep up to 3 of the customer's most recent orders (most-recent-first,
// de-duplicated by content) so the hero "Reorder last delivery" drawer can
// offer a choice between their standard loads — not just the single latest
// order.
//
// IMPORTANT: this uses the `SavedOrder` shape (the reorder payload written
// by the cart), which is DIFFERENT from the `TrackedOrder` shape in
// utils/orderTracking.ts. They are not interchangeable.
//
// The legacy single-order key ('253gravel_last_order') is left untouched —
// other surfaces and older devices still read it. pushRecentOrder is purely
// additive, and getRecentOrders seeds from the legacy key on first read so
// existing customers immediately see their last order in the new drawer.

export type SavedOrderItem = {
  id?: string
  category: string
  product: string
  quantity: number
  pricePerYard: number
}

export type SavedOrder = {
  items: SavedOrderItem[]
  addressInput?: string
  name?: string
  phone?: string
  email?: string
}

const RECENT_ORDERS_KEY = '253gravel_recent_orders'
const LEGACY_LAST_ORDER_KEY = '253gravel_last_order'
// Tracked-order history (TrackedOrder shape, keyed by orderId). Used as a
// backfill so orders placed before this feature shipped — or only ever saved
// for tracking — still appear in the reorder drawer.
const TRACKED_ORDERS_KEY = '253gravel_tracked_orders'
const MAX_RECENT = 3

// Stable content signature used to de-duplicate. Two orders are "the same"
// if they request the same materials/quantities to the same address.
// NOTE: category is intentionally excluded — backfilled tracked orders don't
// carry a category, so keying on product+quantity+address lets the same order
// de-dupe correctly across the recent-orders array and the tracked history.
function normalizeAddress(addr?: string): string {
  return (addr || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 40)
}
function orderSignature(order: SavedOrder): string {
  try {
    const items = (order.items || [])
      .map((i) => `${i.product}|${i.quantity}`)
      .sort()
      .join('~')
    return `${items}@@${normalizeAddress(order.addressInput)}`
  } catch {
    return Math.random().toString(36)
  }
}

// Convert the tracked-order history (TrackedOrder shape) into SavedOrders so
// they can backfill the reorder drawer. TrackedOrderItem has no category, so
// we leave it blank — display only needs product+quantity, and reorder still
// adds the product to the cart.
function readTrackedOrdersAsSaved(): { order: SavedOrder; ts: number }[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(TRACKED_ORDERS_KEY)
    if (!raw) return []
    const all = JSON.parse(raw) as Record<string, any>
    if (!all || typeof all !== 'object') return []
    const out: { order: SavedOrder; ts: number }[] = []
    for (const id of Object.keys(all)) {
      const t = all[id]
      if (!t || !Array.isArray(t.items) || t.items.length === 0) continue
      out.push({
        ts: typeof t.createdAt === 'number' ? t.createdAt : 0,
        order: {
          items: t.items.map((it: any) => ({
            category: '',
            product: it.product,
            quantity: it.quantity,
            pricePerYard: it.pricePerYard,
          })),
          addressInput: t.address,
          name: t.name,
          phone: t.phone,
          email: t.email,
        },
      })
    }
    // Most-recent-first.
    out.sort((a, b) => b.ts - a.ts)
    return out
  } catch {
    return []
  }
}

function isValidOrder(o: unknown): o is SavedOrder {
  return (
    !!o &&
    typeof o === 'object' &&
    Array.isArray((o as SavedOrder).items) &&
    (o as SavedOrder).items.length > 0
  )
}

function readLegacyLastOrder(): SavedOrder | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(LEGACY_LAST_ORDER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isValidOrder(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeRecent(orders: SavedOrder[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(
      RECENT_ORDERS_KEY,
      JSON.stringify(orders.slice(0, MAX_RECENT)),
    )
  } catch {
    /* private window / quota — ignore */
  }
}

// Returns up to 3 recent orders, most-recent-first.
//
// Sources, merged in priority order and de-duped by content signature:
//   1. The recent-orders array (authoritative — carries real categories).
//   2. The legacy single-order key (in case the array is empty).
//   3. The tracked-order history — backfills orders placed before this
//      feature shipped (e.g. an older topsoil order) so the drawer reflects
//      the customer's real recent activity, not just post-feature orders.
export function getRecentOrders(): SavedOrder[] {
  if (typeof window === 'undefined') return []
  const merged: SavedOrder[] = []
  const seen = new Set<string>()
  const add = (o: SavedOrder | null) => {
    if (!isValidOrder(o)) return
    const sig = orderSignature(o)
    if (seen.has(sig)) return
    seen.add(sig)
    merged.push(o)
  }
  // 1. Authoritative recent-orders array.
  try {
    const raw = localStorage.getItem(RECENT_ORDERS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) parsed.filter(isValidOrder).forEach(add)
    }
  } catch {
    /* ignore */
  }
  // 2. Legacy single-order key.
  add(readLegacyLastOrder())
  // 3. Tracked-order history backfill (most-recent-first).
  readTrackedOrdersAsSaved().forEach((t) => add(t.order))
  return merged.slice(0, MAX_RECENT)
}

// Prepend a freshly placed order to the recent list: de-dupe by content,
// cap at 3, most-recent-first. Additive — does not touch the legacy key.
export function pushRecentOrder(order: SavedOrder): void {
  if (typeof window === 'undefined' || !isValidOrder(order)) return
  const existing = getRecentOrders()
  const sig = orderSignature(order)
  const deduped = existing.filter((o) => orderSignature(o) !== sig)
  writeRecent([order, ...deduped])
}

// Clears all saved order history used by the reorder surfaces: the
// recent-orders array, the legacy single-order key, and the tracked-order
// history. After this the hero reverts to the default marketing greeting
// (first-time state) since no saved order remains.
export function clearRecentOrders(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(RECENT_ORDERS_KEY)
    localStorage.removeItem(LEGACY_LAST_ORDER_KEY)
    localStorage.removeItem(TRACKED_ORDERS_KEY)
    localStorage.removeItem('253gravel_latest_order_id')
    localStorage.removeItem('reorder_chip_impressions_v1')
  } catch {
    /* ignore */
  }
}

// Shared display helper so every reorder surface labels an order identically.
export function summarizeOrder(order: SavedOrder): string {
  const items = order.items || []
  if (items.length === 0) return 'Saved order'
  if (items.length > 1) {
    const totalYards = items.reduce((s, i) => s + i.quantity, 0)
    return `${totalYards} yds · ${items.length} materials`
  }
  return `${items[0].quantity} yds ${items[0].product}`
}
